import { useNavigate } from "@tanstack/react-router";
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useContext,
  createContext,
} from "react";

const key = "livelink.auth.role";

type AuthContextType = {
  isAuthenticated: boolean;
  login: (pin: string) => Promise<void>;
  logout: () => void;
  role: string | null;
  isError: boolean;
  isPending: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

const getStoredRole = () => {
  return localStorage.getItem(key);
};

const setStoredRole = (role: string | null) => {
  if (role) {
    localStorage.setItem(key, role);
  } else {
    localStorage.removeItem(key);
  }
};

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [pending, setPending] = useState(false);
  const [role, setRole] = useState<string | null>(getStoredRole());
  const [error, setError] = useState<boolean>(false);

  const isAuthenticated = !!role;
  const isError = !!error;
  const isPending = !!pending;

  const logout = useCallback(() => {
    setStoredRole(null);
    setRole(null);
  }, []);

  const login = useCallback(async (pin: string) => {
    const response = await fetch("/api/authenticate", {
      method: "POST",
      body: JSON.stringify({ pin }),
    });

    if (response.status === 401) {
      setError(true);
      return;
    }
    setPending(true);
    const json = await response.json();

    setError(false);
    setStoredRole(json.role);
    setRole(json.role);

    setTimeout(() => {
      setPending(false);
    }, 1000);
  }, []);

  useEffect(() => {
    setRole(getStoredRole());
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, role, login, logout, isError, isPending }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthenticate = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("NEW useAuth must be used within an AuthProvider");
  }
  return context;
};
